/**
 * /comparisons/chatprd — "Draftlize vs ChatPRD" comparison page.
 * Targets the "chatprd alternative" / "draftlize vs chatprd" / "ai prd"
 * cluster (Ahrefs: "ai prd" parent topic IS chatprd — the SERP treats
 * ChatPRD as the head entity for the query, so an honest comparison is
 * the natural way in).
 *
 * Honesty contract, same as the other comparison pages: ChatPRD is
 * genuinely excellent at zero-to-draft generation and CPO-style
 * coaching — say so plainly. The wedge is everything after the draft:
 * in Draftlize the PRD is a card graph with stale propagation and an
 * MCP surface agents read live, not a document that starts drifting on
 * day one.
 *
 * English-only and inline (no i18n); Chinese twin in ChatPrdZh.tsx.
 */
import { MarketingShell } from '../MarketingShell';

export function ChatPrd() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/comparisons/chatprd"
      documentTitle="ChatPRD Alternative for PRDs That Stay True: Draftlize vs ChatPRD · Draftlize"
      documentDescription="ChatPRD writes you a polished PRD fast — this honest comparison is about what happens after. In Draftlize the PRD is a card graph: change one decision and every dependent flags stale, and your coding agent reads the live version over MCP."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Comparison · vs ChatPRD</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}
          >
            Draftlize vs ChatPRD:<br/>
            <em>the draft, or what happens after?</em>
          </h1>
          <p className="lede">
            Short answer: ChatPRD is the fastest way to go from a one-line idea to a polished <a href="/product-requirements-document" style={{ color: 'var(--amber)' }}>PRD</a>, with genuinely good CPO-style coaching on top. Draftlize starts where that draft ends: the PRD becomes a graph of addressable decision cards, so when one decision changes, everything that depended on it flags stale — and your coding agent reads the live version over MCP instead of a copy. One tool optimizes the writing; the other keeps what you wrote true.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">Side by side, <em>7 dimensions.</em></h2>
          </div>
          <p>Different centers of gravity: ChatPRD is a document generator with coaching; Draftlize is a decision substrate with generation. The rows below are where that difference bites.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>ChatPRD</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>What the AI produces</td><td className="ours yes">Card graph — every decision addressable</td><td className="partial">A polished PRD document</td></tr>
            <tr><td>Zero-to-draft speed</td><td className="partial">Fast, but output is structured cards</td><td className="yes">Excellent — its home turf</td></tr>
            <tr><td>CPO-style coaching on your draft</td><td className="no">— (gap detection on the graph instead)</td><td className="yes">Yes, and it's good</td></tr>
            <tr><td>A decision changes after the draft</td><td className="ours yes">Dependents auto-flag stale</td><td className="no">You re-prompt; synced copies go quietly wrong</td></tr>
            <tr><td>What your coding agent reads</td><td className="ours yes">Live cards over MCP, every turn</td><td className="partial">A doc handed off to Notion / Linear / v0</td></tr>
            <tr><td>Drift between PRD and implementation</td><td className="ours yes">Flips to drift_detected</td><td className="no">Invisible until review</td></tr>
            <tr><td>Team workspace integrations</td><td className="partial">Claude Code / Cursor / MCP + CLI, agent-first</td><td className="yes">12+ — Linear, Notion, Slack, GitHub, Confluence</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Where <em>ChatPRD wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Fastest first draft in the category</h3>
            <p>One line in, a structured, well-formed PRD out. If your bottleneck is blank-page time — you write many PRDs and each one starts from zero — ChatPRD earns its "#1 AI platform for product managers" positioning. Nothing here beats it at that.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Coaching, not just generation</h3>
            <p>ChatPRD's CPO-level feedback pokes at strategic gaps and unstated assumptions in what you wrote. If you're an early-career PM — or the only PM in the building — that standing critique partner is real value Draftlize doesn't offer.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Mature team workflow</h3>
            <p>Shared spaces, custom personas, enterprise controls, and integrations into Linear, Notion, Slack, GitHub, Confluence and AI builders like v0 and Lovable. If PRDs must land inside an existing team stack today, ChatPRD's plumbing is further along.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Where <em>Draftlize wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The PRD survives its first decision change</h3>
            <p>A generated document is correct exactly once — at generation time. In Draftlize the PRD is cards wired to the <a href="/decision-log" style={{ color: 'var(--amber)' }}>decisions</a> under them: flip one upstream decision and every dependent card flags <code>stale</code> on its own, the way a build system invalidates artifacts. No re-reading six pages to find what the change touched.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Agents read the substrate, not a paste</h3>
            <p>ChatPRD hands a finished doc to your tools; from then on, agents work from copies. Draftlize is the substrate agents work in: Claude Code or Cursor pulls the current cards over MCP before writing a line, so the implementation follows the PRD as it is now — not as it was when someone last exported it.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">One decision, cited everywhere</h3>
            <p>The pricing decision lives in one card, cited by the PRD, the <a href="/product-spec-template" style={{ color: 'var(--amber)' }}>spec</a>, and the launch checklist. Contradict it in a later thread and the conflict surfaces. In document land the same decision exists as N paraphrases that drift independently.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          ChatPRD answers "how do I get a great PRD written by Friday." Draftlize answers "how is this PRD still true in March."
        </blockquote>
        <cite>If drafting is the pain, take ChatPRD. If drift is the pain, that's Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'Is Draftlize a ChatPRD alternative?', a: 'For the "AI helps me produce a PRD" job, yes — both do it. But they optimize different halves: ChatPRD optimizes drafting speed and coaching quality; Draftlize optimizes what happens after the draft, when decisions change and documents quietly stop being true. If you only need the fastest polished document, ChatPRD is the better pick.' },
            { q: 'What is the actual difference between ChatPRD and Draftlize?', a: 'The output shape. ChatPRD produces a document you export into your stack. Draftlize produces a card graph: each decision in the PRD is an addressable card with declared dependencies, so a change propagates — dependents flag stale, conflicts surface, and agents read the live graph over MCP instead of a copy.' },
            { q: 'Can I use ChatPRD and Draftlize together?', a: 'Yes, and the seam is clean: draft in ChatPRD if you like its speed and coaching, then land the durable decisions as Draftlize cards. The document remains a snapshot for humans; the cards become the source of truth that agents read and that tracks drift going forward.' },
            { q: 'Does Draftlize generate PRDs with AI too?', a: 'Yes — you can chat a PRD into existence in Draftlize. The difference is the result: instead of one long document, you get structured cards (problem, goals, decisions, requirements) that are individually addressable, individually citable, and wired for stale propagation from day one.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start free with <em>$5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Keep whatever writes your first draft. Draftlize is where the decisions live afterward — see the <a href="/ai-prd" style={{ color: 'var(--amber)' }}>AI PRD workbench</a> or the <a href="/prd-template" style={{ color: 'var(--amber)' }}>PRD template that doesn't drift</a>.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
